import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Tag,
  useColorModeValue,
  Center,
  Spinner,
  Grid,
  Progress,
  Badge,
  Divider,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useBreakpointValue,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

import { Panel, AnimatedCard, containerVariants, cardVariants, PageHeading } from "@bemap/ui";
import { ANALYST_ROUTES, formatDate, IAssessment, IAssessmentResults, openUrl, RoleLevel, useAuth } from "@bemap/utils";
import { downloadAssessmentReport, getAssessmentById } from "../../services/assessments-service";
import { MdDownload } from "react-icons/md";

interface AssessmentResultsPageProps {
  assessmentId?: string;
}

// Reusable Components
const BehavioralProfilesSection = ({ results, cardBg, textColor, subtitleColor, t }: any) => (
  <Grid templateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={4} w="100%">
    {results.profilesChartData.map((profile: any) => (
      <AnimatedCard key={profile.id} bg={cardBg} variants={cardVariants}>
        <VStack spacing={3} align="stretch">
          <HStack justify="space-between">
            <Text fontWeight="bold" color={textColor}>
              {profile.name}
            </Text>
            <Tag size="sm" colorScheme="blue">
              {profile.letter}
            </Tag>
          </HStack>
          <Box>
            <Text fontSize="sm" color={subtitleColor} mb={1}>
              {t('analyst.type1') || 'Type 1'}: {profile.type1.toFixed(1)}%
            </Text>
            <Progress
              value={profile.type1}
              colorScheme="blue"
              size="sm"
              borderRadius="md"
            />
          </Box>
          <Box>
            <Text fontSize="sm" color={subtitleColor} mb={1}>
              {t('analyst.type2') || 'Type 2'}: {profile.type2.toFixed(1)}%
            </Text>
            <Progress
              value={profile.type2}
              colorScheme="green"
              size="sm"
              borderRadius="md"
            />
          </Box>
          <Box>
            <Text fontSize="sm" color={subtitleColor} mb={1}>
              {t('analyst.intensity') || 'Intensity'}: {profile.intensity.toFixed(1)}%
            </Text>
            <Progress
              value={profile.intensity}
              colorScheme="purple"
              size="sm"
              borderRadius="md"
            />
          </Box>
        </VStack>
      </AnimatedCard>
    ))}
  </Grid>
);

const SituationalIndexesSection = ({ results, cardBg, textColor, subtitleColor, getIntensityColor }: any) => (
  <Grid templateColumns="repeat(auto-fit, minmax(300px, 1fr))" gap={4} w="100%">
    {results.situationalIndexChartData.map((index: any) => (
      <AnimatedCard key={index.id} bg={cardBg} variants={cardVariants}>
        <VStack spacing={3} align="stretch">
          <HStack justify="space-between">
            <Text fontWeight="bold" color={textColor}>
              {index.name}
            </Text>
            <Badge colorScheme={getIntensityColor(index.intensityName)}>
              {index.intensityName}
            </Badge>
          </HStack>
          <Box>
            <Text fontSize="sm" color={subtitleColor} mb={1}>
              {index.inner.toFixed(1)}%
            </Text>
            <Progress
              value={index.inner}
              colorScheme={getIntensityColor(index.intensityName)}
              size="lg"
              borderRadius="md"
            />
          </Box>
          <Text fontSize="sm" color={subtitleColor}>
            {index.text}
          </Text>
        </VStack>
      </AnimatedCard>
    ))}
  </Grid>
);

const CompetencesSection = ({ results, cardBg, textColor, subtitleColor }: any) => (
  <Grid templateColumns="repeat(auto-fit, minmax(350px, 1fr))" gap={4} w="100%">
    {results.competencesChartData.map((competence: any) => (
      <AnimatedCard key={competence.id} bg={cardBg} variants={cardVariants}>
        <VStack spacing={3} align="stretch">
          <HStack justify="space-between">
            <Text fontWeight="bold" color={textColor}>
              {competence.name}
            </Text>
          </HStack>
          <Box>
            <Text fontSize="sm" color={subtitleColor} mb={1}>
              {competence.inner.toFixed(1)}%
            </Text>
            <Progress
              value={competence.inner}
              colorScheme="blue"
              size="lg"
              borderRadius="md"
            />
          </Box>
          <Text fontSize="sm" color={subtitleColor}>
            {competence.text}
          </Text>
          <Divider />
          <Grid templateColumns="repeat(2, 1fr)" gap={2}>
            {competence.profile1 ? (
              <Box>
                <Text fontSize="xs" color={subtitleColor} mb={1}>
                  Executor
                </Text>
                <Progress
                  value={competence.profile1}
                  colorScheme="red"
                  size="sm"
                  borderRadius="md"
                />
              </Box>
            ) : null}
            {competence.profile2 ? (
              <Box>
                <Text fontSize="xs" color={subtitleColor} mb={1}>
                  Comunicador
                </Text>
                <Progress
                  value={competence.profile2}
                  colorScheme="yellow"
                  size="sm"
                  borderRadius="md"
                />
              </Box>
            ) : null}
            {competence.profile3 ? (
              <Box>
                <Text fontSize="xs" color={subtitleColor} mb={1}>
                  Planejador
                </Text>
                <Progress
                  value={competence.profile3}
                  colorScheme="green"
                  size="sm"
                  borderRadius="md"
                />
              </Box>
            ) : null}
            {competence.profile4 ? (
              <Box>
                <Text fontSize="xs" color={subtitleColor} mb={1}>
                  Analista
                </Text>
                <Progress
                  value={competence.profile4}
                  colorScheme="blue"
                  size="sm"
                  borderRadius="md"
                />
              </Box>
            ) : null}
          </Grid>
        </VStack>
      </AnimatedCard>
    ))}
  </Grid>
);

const TalentsSection = ({ results, cardBg, textColor, subtitleColor }: any) => (
  <Grid templateColumns="repeat(auto-fit, minmax(300px, 1fr))" gap={4} w="100%">
    {results.talents.map((talent: any) => (
      <AnimatedCard key={talent.id} bg={cardBg} variants={cardVariants}>
        <VStack spacing={3} align="stretch">
          <Text fontWeight="bold" color={textColor}>
            {talent.name}
          </Text>
          <Box>
            <Text fontSize="sm" color={subtitleColor} mb={1}>
              Inner: {talent.inner.toFixed(1)}%
            </Text>
            <Progress
              value={talent.inner}
              colorScheme="blue"
              size="sm"
              borderRadius="md"
            />
          </Box>
          <Box>
            <Text fontSize="sm" color={subtitleColor} mb={1}>
              Environment: {talent.environment.toFixed(1)}%
            </Text>
            <Progress
              value={talent.environment}
              colorScheme="green"
              size="sm"
              borderRadius="md"
            />
          </Box>
          <Text fontSize="sm" color={subtitleColor}>
            {talent.text}
          </Text>
        </VStack>
      </AnimatedCard>
    ))}
  </Grid>
);

const AssessmentResultsPage = ({ assessmentId }: AssessmentResultsPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const params = useParams();
  const id = assessmentId || params.id;

  const [isLoading, setIsLoading] = useState(true);
  const [assessment, setAssessment] = useState<IAssessment | null>(null);
  const [results, setResults] = useState<IAssessmentResults | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const cardBg = useColorModeValue("white", "gray.700");
  const textColor = useColorModeValue("gray.700", "white");
  const subtitleColor = useColorModeValue("gray.500", "gray.100");

  const MotionBox = motion(Box);

  // Move useBreakpointValue to top level to avoid conditional hook calls
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    if (id) {
      loadAssessment();
    }
  }, [id]);

  const loadAssessment = async () => {
    setIsLoading(true);
    try {
      const response = await getAssessmentById(parseInt(id!));
      setAssessment(response.message);
      setResults(response.message.results);
      setIsLoading(false);
    } catch (error) {
      toast({
        title: t('general.error') || 'Error',
        description: t('general.loading_error') || 'Failed to load assessment',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsLoading(false);
    }
  };

  async function downloadReport() {
    try {
      setIsSaving(true);
      const { message } = await downloadAssessmentReport(assessment?.hash || '');
      openUrl(message);
    } catch (error) {
      toast({
        title: t('general.error') || 'Error',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  }

  const getIntensityColor = (intensity: string) => {
    switch (intensity.toLowerCase()) {
      case 'muito baixa':
        return 'red';
      case 'baixa':
        return 'orange';
      case 'normal baixa':
        return 'yellow';
      case 'normal alta':
        return 'blue';
      case 'alta':
        return 'green';
      default:
        return 'gray';
    }
  };

  if (isLoading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!assessment || !results) {
    return (
      <VStack spacing={4} p={4}>
        <Text>{t('general.no_data_available') || 'No data available'}</Text>
        <Button leftIcon={<ArrowBackIcon />} onClick={() => navigate('/assessments/all')}>
          {t('actions.back') || 'Back'}
        </Button>
      </VStack>
    );
  }

  // Shared props for all sections
  const sectionProps = {
    results,
    cardBg,
    textColor,
    subtitleColor,
    t,
    getIntensityColor,
  };

  return (
    <MotionBox
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      as={VStack}
      spacing={6}
      align="stretch"
    >
      {/* Header */}
      <HStack justify="space-between" pt={4}>
        <PageHeading title={t('analyst.assessment_results')} actions={[
          {
            label: t('analyst.assesments_action_download'),
            onClick: () => downloadReport(),
            icon: <MdDownload />,
            isLoading: isSaving,
          },
          {
            label: t('actions.back') || 'Back',
            onClick: () => navigate(ANALYST_ROUTES.ASSESSMENTS),
            icon: <ArrowBackIcon />,
            variant: 'outline',
          }
        ]} />
      </HStack>

      {/* Person Information Panel */}
      <Panel>
        <VStack spacing={4} p={6}>
          <Grid templateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={4} w="100%">
            <VStack align="start" spacing={2}>
              <Text fontSize="sm" color={subtitleColor}>
                {t('analyst.dominant_profile') || 'Dominant Profile'}
              </Text>
              <HStack spacing={2}>
                <Tag size="md" colorScheme="blue" variant="subtle">
                  {results.dominantProfiles.short}
                </Tag>
              </HStack>
            </VStack>
            <VStack align="start" spacing={2}>
              <Text fontSize="sm" color={subtitleColor}>
                {t('analyst.assesments_table_name') || 'Name'}
              </Text>
              <Text fontWeight="medium" color={textColor}>
                {assessment.profile?.name}
              </Text>
            </VStack>
            <VStack align="start" spacing={2}>
              <Text fontSize="sm" color={subtitleColor}>
                {t('analyst.assesments_table_email') || 'Email'}
              </Text>
              <Text fontWeight="medium" color={textColor}>
                {assessment.profile?.email}
              </Text>
            </VStack>
            <VStack align="start" spacing={2}>
              <Text fontSize="sm" color={subtitleColor}>
                {t('account.phone') || 'Phone'}
              </Text>
              <Text fontWeight="medium" color={textColor}>
                {assessment.profile?.phone || t('general.not_available') || 'N/A'}
              </Text>
            </VStack>
            {user?.permission === RoleLevel.ADMIN ? (
              <>
                <VStack align="start" spacing={2}>
                  <Text fontSize="sm" color={subtitleColor}>
                    {t('analyst.team_name') || 'Team Name'}
                  </Text>
                  <Text fontWeight="medium" color={textColor}>
                    {assessment.team?.name || t('general.not_available') || 'N/A'}
                  </Text>
                </VStack>
                <VStack align="start" spacing={2}>
                  <Text fontSize="sm" color={subtitleColor}>
                    {t('analyst.team_email') || 'Team Email'}
                  </Text>
                  <Text fontWeight="medium" color={textColor}>
                    {assessment.team?.user?.email || t('general.not_available') || 'N/A'}
                  </Text>
                </VStack>
              </>
            ) : null}
            <VStack align="start" spacing={2}>
              <Text fontSize="sm" color={subtitleColor}>
                {t('analyst.assesments_table_created') || 'Created At'}
              </Text>
              <Text fontWeight="medium" color={textColor}>
                {formatDate(assessment.created_at || '')}
              </Text>
            </VStack>
            <VStack align="start" spacing={2}>
              <Text fontSize="sm" color={subtitleColor}>
                {t('analyst.assessment_answered_at') || 'Answered At'}
              </Text>
              <Text fontWeight="medium" color={textColor}>
                {assessment.updated_at ? formatDate(assessment.updated_at) : t('general.not_available') || 'N/A'}
              </Text>
            </VStack>
          </Grid>
        </VStack>
      </Panel>

      {/* Responsive Results Sections */}
      {assessment.report_id === 1 ? (
        // Simple view for report_id === 1: only Behavioral Profiles
        <VStack spacing={4}>
          <BehavioralProfilesSection {...sectionProps} />
        </VStack>
      ) : (
        // Full view for other report_ids
        <>
          {isMobile ? (
            // Mobile: Accordion
            <Accordion allowToggle>
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <Text fontWeight="bold" color={textColor}>
                      {t('analyst.behavioral_profiles') || 'Behavioral Profiles'}
                    </Text>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <VStack spacing={4}>
                    <BehavioralProfilesSection {...sectionProps} />
                  </VStack>
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <Text fontWeight="bold" color={textColor}>
                      {t('analyst.situational_indexes') || 'Situational Indexes'}
                    </Text>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <VStack spacing={4}>
                    <SituationalIndexesSection {...sectionProps} />
                  </VStack>
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <Text fontWeight="bold" color={textColor}>
                      {t('analyst.competences') || 'Competences'}
                    </Text>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <VStack spacing={4}>
                    <CompetencesSection {...sectionProps} />
                  </VStack>
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <Text fontWeight="bold" color={textColor}>
                      {t('analyst.talents') || 'Talents'}
                    </Text>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <VStack spacing={4}>
                    <TalentsSection {...sectionProps} />
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          ) : (
            // Desktop: Tabs
            <Tabs variant="enclosed" colorScheme="blue">
              <TabList>
                <Tab>{t('analyst.behavioral_profiles') || 'Behavioral Profiles'}</Tab>
                <Tab>{t('analyst.situational_indexes') || 'Situational Indexes'}</Tab>
                <Tab>{t('analyst.competences') || 'Competences'}</Tab>
                <Tab>{t('analyst.talents') || 'Talents'}</Tab>
              </TabList>

              <TabPanels>
                <TabPanel>
                  <VStack spacing={4}>
                    <BehavioralProfilesSection {...sectionProps} />
                  </VStack>
                </TabPanel>

                <TabPanel>
                  <VStack spacing={4}>
                    <SituationalIndexesSection {...sectionProps} />
                  </VStack>
                </TabPanel>

                <TabPanel>
                  <VStack spacing={4}>
                    <CompetencesSection {...sectionProps} />
                  </VStack>
                </TabPanel>

                <TabPanel>
                  <VStack spacing={4}>
                    <TalentsSection {...sectionProps} />
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </>
      )}
    </MotionBox>
  );
};

export default AssessmentResultsPage; 